
import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from 'react-responsive-modal';
import { useTranslation } from 'react-i18next';
import { InstallationsContext } from './App';
import { not_null_or_undefined, null_or_undefined } from '../utilities';
import Select from 'react-select';
import cloneDeep from 'lodash/cloneDeep';
import { confirmAlert } from "react-confirm-alert";

const emptyModal = { installation: null, loading: false };

export default function InstallationInfoEdit({ open, setOpen, installation, canEditAlias, canEditInfo }) {
  const { t } = useTranslation();
  const {
    selectedInst,
    updateInstallationInfo,
    requestCountries
  } = React.useContext(InstallationsContext);

  const wizardSoundCustomSelectStyles = {
    option: (provided, state) => ({
      ...provided,
      width: '100%',
    }),
    control: (provided, state) => ({
      ...provided,
      width: '100%',
    }),
    valueContainer: (provided, state) => ({
      ...provided,
      width: '100%',
    })
  };

  const [modalInfo, setModalInfo] = useState(emptyModal);
  const [countriesOptions, setCountriesOptions] = useState(null);
  const [mustUpdateInfo, setMustUpdateInfo] = useState(false);

  const closeModal = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  function changedFields(newInfo, oldInfo) {
    let changed = {id: oldInfo.id};
    if(newInfo.title !== oldInfo.title) {
      if(!(newInfo.title === "" && oldInfo.title === null))
        changed.title = newInfo.title;
    }
    if(newInfo.description !== oldInfo.description) {
      if(!(newInfo.description === "" && oldInfo.description === null))
        changed.description = newInfo.description;
    }
    if(newInfo.note !== oldInfo.note) {
      if(!(newInfo.note === "" && oldInfo.note === null))
        changed.note = newInfo.note;
    }
    if(newInfo.addressLine1 !== oldInfo.addressLine1) {
      changed.addressLine1 = newInfo.addressLine1;
    }
    if(newInfo.addressLine2 !== oldInfo.addressLine2) {
      if(!(newInfo.addressLine2 === "" && oldInfo.addressLine2 === null))
        changed.addressLine2 = newInfo.addressLine2;
    }
    if(newInfo.city !== oldInfo.city) {
      changed.city = newInfo.city;
    }
    if(newInfo.zipCode !== oldInfo.zipCode) {
      changed.zipCode = newInfo.zipCode;
    }
    if(newInfo.province !== oldInfo.province) {
      changed.province = newInfo.province;
    }
    if(newInfo.region !== oldInfo.region) {
      changed.region = newInfo.region;
    }
    if(newInfo.country.code !== oldInfo.country.code) {
      changed.country = {code: newInfo.country.code};
    }
    if(newInfo.floor !== oldInfo.floor) {
      if(!(newInfo.floor === "" && oldInfo.floor === null))
        changed.floor = newInfo.floor;
    }
    if(newInfo.nameTag !== oldInfo.nameTag) {
      if(!(newInfo.nameTag === "" && oldInfo.nameTag === null))
        changed.nameTag = newInfo.nameTag;
    }
    if(newInfo.addressNote !== oldInfo.addressNote) {
      if(!(newInfo.addressNote === "" && oldInfo.addressNote === null))
        changed.addressNote = newInfo.addressNote;
    }
    return changed;
  }

  useEffect(() => {
    if (!open) {
      setMustUpdateInfo(false);
      setModalInfo(emptyModal);
    }
  }, [open]);

  useEffect(() => {
    if (open === false) {
      return;
    }

    if (installation && modalInfo.installation === null && modalInfo.loading === false) {
      let apiInstallation = {
        id: installation.id
      }
      if(canEditAlias) {
        apiInstallation.title = installation.title;
        apiInstallation.description = installation.description;
      }
      if(canEditInfo) {
        apiInstallation.note = installation.note;
        apiInstallation.addressLine1 = installation.addressLine1;
        apiInstallation.addressLine2 = installation.addressLine2;
        apiInstallation.country = {code: installation.country.code, name: installation.country.name};
        apiInstallation.region = installation.region;
        apiInstallation.province = installation.province;
        apiInstallation.city = installation.city;
        apiInstallation.zipCode = installation.zipCode;
        apiInstallation.floor = installation.floor;
        apiInstallation.nameTag = installation.nameTag;
        apiInstallation.addressNote = installation.addressNote;
      }

      setModalInfo({ installation: apiInstallation, loading: false });
    }
  }, [installation, modalInfo, open]);

  useEffect(() => {
    async function loadCountries() {
      const countries = await requestCountries();
      if (null_or_undefined(countries)) {
        return [];
      }
      let options = [];
      countries.forEach((name, code) => {
        options.push({ value: code, label: name });
      });
      return setCountriesOptions(options);
    }

    if (countriesOptions === null) {
      loadCountries();
    }
  }, [countriesOptions, requestCountries]);

  async function saveInstallationInfo() {
    if (!mustUpdateInfo) {
      closeModal();
      return;
    }

    // Verifica campi vuoti (escludendo note e addressNote)
    const emptyFields = [];
    if(canEditAlias) {
      if (!modalInfo.installation.title || modalInfo.installation.title.trim() === '') {
        emptyFields.push(t('installationInfoEdit.title'));
      }
      if (!modalInfo.installation.description || modalInfo.installation.description.trim() === '') {
        emptyFields.push(t('installationInfoEdit.description'));
      }
    }
    if(canEditInfo) {
      if (!modalInfo.installation.addressLine1 || modalInfo.installation.addressLine1.trim() === '') {
        emptyFields.push(t('installationInfoEdit.addressLine1'));
      }
      if (!modalInfo.installation.country.code || modalInfo.installation.country.code === '') {
        emptyFields.push(t('installationInfoEdit.country'));
      }
      if (!modalInfo.installation.region || modalInfo.installation.region.trim() === '') {
        emptyFields.push(t('installationInfoEdit.region'));
      }
      if (!modalInfo.installation.province || modalInfo.installation.province.trim() === '') {
        emptyFields.push(t('installationInfoEdit.province'));
      }
      if (!modalInfo.installation.city || modalInfo.installation.city.trim() === '') {
        emptyFields.push(t('installationInfoEdit.city'));
      }
      if (!modalInfo.installation.zipCode || modalInfo.installation.zipCode.trim() === '') {
        emptyFields.push(t('installationInfoEdit.zipCode'));
      }
      if (!modalInfo.installation.floor || modalInfo.installation.floor.trim() === '') {
        emptyFields.push(t('installationInfoEdit.floor'));
      }
      if (!modalInfo.installation.nameTag || modalInfo.installation.nameTag.trim() === '') {
        emptyFields.push(t('installationInfoEdit.nameTag'));
      }
    }

    // Mostra messaggio di conferma con campi vuoti se presenti
    const confirmMessage = emptyFields.length > 0
      ? `${t('installationInfoEdit.confirmSaveWithEmptyFields')}\n\n${t('installationInfoEdit.emptyFields')}: ${emptyFields.join(', ')}`
      : t('installationInfoEdit.confirmSave');

    confirmAlert({
      title: t('installationInfoEdit.confirmTitle'),
      message: confirmMessage,
      buttons: [
        {
          label: t('Yes'),
          onClick: async () => {
            try {
              const res = await updateInstallationInfo(selectedInst, changedFields(modalInfo.installation, installation));
              if (not_null_or_undefined(res.userMessage)) {
                // Mostra errore in popup modale
                confirmAlert({
                  title: t('Error'),
                  message: res.userMessage || res,
                  buttons: [{ label: t('OK') }],
                  overlayClassName: "overlay-confirm-alert-over-all wide"
                });
                return;
              }
              closeModal();
            } catch (error) {
              // Gestione errori generici
              confirmAlert({
                title: t('Error'),
                message: error.message || t('installationInfoEdit.genericError'),
                buttons: [{ label: t('OK') }],
                overlayClassName: "overlay-confirm-alert-over-all wide"
              });
            }
          },
        },
        {
          label: t('No'),
        },
      ],
      overlayClassName: "overlay-confirm-alert-over-all wide"
    });
  }

  return (
    <Modal
      onClose={closeModal}
      open={open}
      styles={{ modal: { minWidth: 'min(80%, 1200px)' } }}
    >
      {(modalInfo.loading === true || modalInfo.installation === null) && (
        <div className="modal-dialog-container">
          <h3>
            {`${t('installationInfoEdit.modalTitle')}`}
            <hr />
          </h3>
          <p>{t('installationInfoEdit.loading')}</p>
        </div>
      )}
      {(modalInfo.loading === false && modalInfo.installation !== null) && (
        <div className="modal-dialog-container col3">
          <h3>
            {`${t('installationInfoEdit.modalTitle')}`}
            <hr />
          </h3>

          {(canEditAlias) && (
              <>
                <div>
                  <label className="input-label" htmlFor="installation-title">
                    {`${t('installationInfoEdit.title')}`}
                  </label>
                  <input
                      className="modal-dialog-input"
                      id="installation-title"
                      name="installation-title"
                      onChange={(e) => {
                        const val = e.target.value;
                        setModalInfo((cur) => {
                          cur.installation.title = val.trim().length > 0 ? val : "";
                          return cloneDeep(cur);
                        });
                        setMustUpdateInfo(true);
                      }}
                      type="text"
                      value={modalInfo.installation.title !== null ? modalInfo.installation.title : ''}
                  />
                </div>

                <div>
                  <label className="input-label" htmlFor="installation-description">
                    {`${t('installationInfoEdit.description')}`}
                  </label>
                  <textarea
                      className="modal-dialog-input"
                      id="installation-description"
                      name="installation-description"
                      onChange={(e) => {
                        const val = e.target.value;
                        setModalInfo((cur) => {
                          cur.installation.description = val.trim().length > 0 ? val : "";
                          return cloneDeep(cur);
                        });
                        setMustUpdateInfo(true);
                      }}
                      rows="3"
                      value={modalInfo.installation.description !== null ? modalInfo.installation.description : ''}
                  />
                </div>
              </>
          )}

          {(canEditInfo) && (
              <>
                <div>
                  <label className="input-label" htmlFor="installation-note">
                    {`${t('installationInfoEdit.note')}`}
                  </label>
                  <textarea
                      className="modal-dialog-input"
                      id="installation-note"
                      name="installation-note"
                      onChange={(e) => {
                        const val = e.target.value;
                        setModalInfo((cur) => {
                          cur.installation.note = val.trim().length > 0 ? val : "";
                          return cloneDeep(cur);
                        });
                        setMustUpdateInfo(true);
                      }}
                      rows="3"
                      value={modalInfo.installation.note !== null ? modalInfo.installation.note : ''}
                  />
                </div>

                <div>
                  <label className="input-label" htmlFor="installation-address-line1">
                    {`${t('installationInfoEdit.addressLine1')}`}
                  </label>
                  <input
                      className="modal-dialog-input"
                      id="installation-address-line1"
                      name="installation-address-line1"
                      onChange={(e) => {
                        const val = e.target.value;
                        setModalInfo((cur) => {
                          cur.installation.addressLine1 = val.trim().length > 0 ? val : null;
                          return cloneDeep(cur);
                        });
                        setMustUpdateInfo(true);
                      }}
                      type="text"
                      value={modalInfo.installation.addressLine1 !== null ? modalInfo.installation.addressLine1 : ''}
                  />
                </div>

                <div>
                  <label className="input-label" htmlFor="installation-address-line2">
                    {`${t('installationInfoEdit.addressLine2')}`}
                  </label>
                  <input
                      className="modal-dialog-input"
                      id="installation-address-line2"
                      name="installation-address-line2"
                      onChange={(e) => {
                        const val = e.target.value;
                        setModalInfo((cur) => {
                          cur.installation.addressLine2 = val.trim().length > 0 ? val : "";
                          return cloneDeep(cur);
                        });
                        setMustUpdateInfo(true);
                      }}
                      type="text"
                      value={modalInfo.installation.addressLine2 !== null ? modalInfo.installation.addressLine2 : ''}
                  />
                </div>

                <div className="reminder-info">
                  <label className="input-label" htmlFor="installation-country"
                         style={{textAlign: "center"}}>{`${t('installationInfoEdit.country')}`}</label>
                  <br/>
                  <Select
                      className="dropdown-select"
                      classNamePrefix="select"
                      defaultValue={
                        {
                          value: modalInfo.installation.country.code,
                          label: modalInfo.installation.country.name
                        }
                      }
                      isClearable={false}
                      isSearchable={true}
                      maxMenuHeight={150}
                      menuPlacement="top"
                      name="installation-country"
                      onChange={(option) => {
                        setModalInfo((cur) => {
                          cur.installation.country.code = option.value;
                          cur.installation.country.name = option.label;
                          return cloneDeep(cur);
                        })
                        setMustUpdateInfo(true);
                      }}
                      options={countriesOptions}
                      styles={wizardSoundCustomSelectStyles}
                  />
                </div>

                <div>
                  <label className="input-label" htmlFor="installation-region">
                    {`${t('installationInfoEdit.region')}`}
                  </label>
                  <input
                      className="modal-dialog-input"
                      id="installation-region"
                      name="installation-region"
                      onChange={(e) => {
                        const val = e.target.value;
                        setModalInfo((cur) => {
                          cur.installation.region = val.trim().length > 0 ? val : null;
                          return cloneDeep(cur);
                        });
                        setMustUpdateInfo(true);
                      }}
                      type="text"
                      value={modalInfo.installation.region !== null ? modalInfo.installation.region : ''}
                  />
                </div>

                <div>
                  <label className="input-label" htmlFor="installation-province">
                    {`${t('installationInfoEdit.province')}`}
                  </label>
                  <input
                      className="modal-dialog-input"
                      id="installation-province"
                      name="installation-province"
                      onChange={(e) => {
                        const val = e.target.value;
                        setModalInfo((cur) => {
                          cur.installation.province = val.trim().length > 0 ? val : null;
                          return cloneDeep(cur);
                        });
                        setMustUpdateInfo(true);
                      }}
                      type="text"
                      value={modalInfo.installation.province !== null ? modalInfo.installation.province : ''}
                  />
                </div>

                <div>
                  <label className="input-label" htmlFor="installation-city">
                    {`${t('installationInfoEdit.city')}`}
                  </label>
                  <input
                      className="modal-dialog-input"
                      id="installation-city"
                      name="installation-city"
                      onChange={(e) => {
                        const val = e.target.value;
                        setModalInfo((cur) => {
                          cur.installation.city = val.trim().length > 0 ? val : null;
                          return cloneDeep(cur);
                        });
                        setMustUpdateInfo(true);
                      }}
                      type="text"
                      value={modalInfo.installation.city !== null ? modalInfo.installation.city : ''}
                  />
                </div>

                <div>
                  <label className="input-label" htmlFor="installation-zip-code">
                    {`${t('installationInfoEdit.zipCode')}`}
                  </label>
                  <input
                      className="modal-dialog-input"
                      id="installation-zip-code"
                      name="installation-zip-code"
                      onChange={(e) => {
                        const val = e.target.value;
                        setModalInfo((cur) => {
                          cur.installation.zipCode = val.trim().length > 0 ? val : null;
                          return cloneDeep(cur);
                        });
                        setMustUpdateInfo(true);
                      }}
                      type="text"
                      value={modalInfo.installation.zipCode !== null ? modalInfo.installation.zipCode : ''}
                  />
                </div>

                <div>
                  <label className="input-label" htmlFor="installation-floor">
                    {`${t('installationInfoEdit.floor')}`}
                  </label>
                  <input
                      className="modal-dialog-input"
                      id="installation-floor"
                      name="installation-floor"
                      onChange={(e) => {
                        const val = e.target.value;
                        setModalInfo((cur) => {
                          cur.installation.floor = val.trim().length > 0 ? val : "";
                          return cloneDeep(cur);
                        });
                        setMustUpdateInfo(true);
                      }}
                      type="text"
                      value={modalInfo.installation.floor !== null ? modalInfo.installation.floor : ''}
                  />
                </div>

                <div>
                  <label className="input-label" htmlFor="installation-name-tag">
                    {`${t('installationInfoEdit.nameTag')}`}
                  </label>
                  <input
                      className="modal-dialog-input"
                      id="installation-name-tag"
                      name="installation-name-tag"
                      onChange={(e) => {
                        const val = e.target.value;
                        setModalInfo((cur) => {
                          cur.installation.nameTag = val.trim().length > 0 ? val : "";
                          return cloneDeep(cur);
                        });
                        setMustUpdateInfo(true);
                      }}
                      type="text"
                      value={modalInfo.installation.nameTag !== null ? modalInfo.installation.nameTag : ''}
                  />
                </div>

                <div>
                  <label className="input-label" htmlFor="installation-address-note">
                    {`${t('installationInfoEdit.addressNote')}`}
                  </label>
                  <textarea
                      className="modal-dialog-input"
                      id="installation-address-note"
                      name="installation-address-note"
                      onChange={(e) => {
                        const val = e.target.value;
                        setModalInfo((cur) => {
                          cur.installation.addressNote = val.trim().length > 0 ? val : "";
                          return cloneDeep(cur);
                        });
                        setMustUpdateInfo(true);
                      }}
                      rows="3"
                      value={modalInfo.installation.addressNote !== null ? modalInfo.installation.addressNote : ''}
                  />
                </div>
              </>
          )}
          <button className="cbutton modal-buttons cancel-button first-in-grid-row" onClick={() => {
            closeModal();
          }} type="button">{`${t('CANCEL')}`}</button>
          <button className="cbutton modal-buttons ok-button last-in-grid-row" onClick={() => {
            saveInstallationInfo();
          }} type="button">{`${t('SAVE')}`}</button>

        </div>
      )}
    </Modal>
  )
}
